<script lang="ts">
  import { fly, fade, slide } from "svelte/transition"
  import { xhr } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import Button from "$app/components/ButtonSpinner.svelte"
  import { ArrowRight, ArrowLeft, X, Lock } from "lucide-svelte"

  import Modal from "$app/components/Modal.svelte"
  import PurchaseCourse from "$app/Pages/Plans/Courses/PurchaseCourse.svelte"
  import PreviewCourse from "$app/Pages/Plans/Courses/PreviewCourse.svelte"
  import { appStore } from "$app/store.svelte"

  import CourseNav from "./CourseNav.svelte"
  import Module1 from "$app/Pages/Plans/Courses/e1/Module1.svelte"
  import Module2 from "$app/Pages/Plans/Courses/e1/Module2.svelte"
  import Module3 from "$app/Pages/Plans/Courses/e1/Module3.svelte"
  import Module4 from "$app/Pages/Plans/Courses/e1/Module4.svelte"
  import Module5 from "$app/Pages/Plans/Courses/e1/Module5.svelte"
  import Module6 from "$app/Pages/Plans/Courses/e1/Module6.svelte"
  import Module7 from "$app/Pages/Plans/Courses/e1/Module7.svelte"
  import Module8 from "$app/Pages/Plans/Courses/e1/Module8.svelte"
  import Module9 from "$app/Pages/Plans/Courses/e1/Module9.svelte"

  interface Props {
    course_progress: any
    stripe_publish_key?: string | null
    stripe_product?: any | null
    show?: boolean
  }

  let {
    course_progress,
    stripe_publish_key = null,
    stripe_product = null,
    show = false
  }: Props = $props()

  let course = appStore.course

  let x = screen.width * 0.5

  import access_course from "@images/courses/access-course.png"
  import close_course from "@images/courses/close-course.png"

  let show_assets = $state(false)

  function toggleCourse() {
    show = !show
  }

  import pdf_download from "@images/courses/pdf-download.png"
  import pdf_download_hover from "@images/courses/pdf-download-hover.png"

  let openPurchaseModal = $state(false)
  let openPreviewModal = $state(true)

  function purchaseCourse() {
    openPurchaseModal = true
  }

  function loadSubstep(substep) {
    loadStep(course_progress.step, substep)
  }

  function updateProgress(event) {
    course_progress = event.detail

    openPurchaseModal = false

    var newURL = location.href.split("?")[0]
    window.history.pushState("object", document.title, newURL)
  }

  function previousStep() {
    let step = course_progress.step
    let substep = course_progress.substep - 1
    if (substep <= 0 && step > 1) {
      step = step - 1
      substep = course.steps[step].substeps.length
    } else if (substep <= 0 && step === 1) {
      substep = 1
    }

    loadStep(step, substep)
  }

  let loadingStep = $state(false)
  async function loadStep(step, substep): Promise<void> {
    loadingStep = true

    try {
      const resp = await xhr.get(
        `/plans/${appStore.plan.id}/courses/e1.json?step=${step}&substep=${substep}`
      )
      course_progress = resp.data.course_progress
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
    } finally {
      loadingStep = false
    }
  }

  let savingProgress = $state(false)
  async function saveProgress(step, substep): Promise<void> {
    savingProgress = true

    try {
      const resp = await xhr.patch(
        `/plans/${appStore.plan.id}/courses/e1/${course.id}.json`,
        {
          course_progress: {
            completed_step: step,
            completed_substep: substep
          }
        }
      )
      course_progress = resp.data
    } catch (e) {
      Honeybadger.notify(e)
    } finally {
      savingProgress = false
    }
  }
</script>

{#if show}
  <div
    class="relative z-10"
    aria-labelledby="slide-over-title"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="fixed inset-0"
      style="background: rgba(0, 0, 0, .75)"
      transition:fade
    ></div>

    <div class="fixed inset-0 overflow-hidden">
      <div
        class="absolute inset-0 overflow-hidden"
        transition:fly={{ x: x, opacity: 1, duration: 1000 }}
      >
        <div class="fixed inset-y-0 right-0 flex max-w-full pl-10">
          <div class="w-screen max-w-4xl z-30">
            <button
              onclick={toggleCourse}
              type="button"
              data-name="close-course"
            >
              <img
                src={close_course}
                class="z-10 w-12 absolute"
                style="top: 36%; left: -8px;"
                alt="Close Course"
                transition:fly={{ x: 0, opacity: 1, duration: 500 }}
              />
            </button>
            <div
              class="flex h-full flex-col bg-white overflow-y-scroll shadow-xl"
            >
              <div class="px-4 sm:px-6 bg-lwp-purple py-3 md:py-6">
                <div
                  class="flex items-start justify-center md:justify-start flex-wrap-reverse"
                >
                  {#key course_progress}
                    <CourseNav
                      {course}
                      {course_progress}
                      onloadSubstep={loadSubstep}
                    />
                  {/key}

                  <div
                    class="md:ml-3 flex justify-end w-full items-start mt-3 md:mt-0"
                  >
                    {#if course_progress.step == 1 && course_progress.substep == 1}
                      <button
                        class="py-2 px-4 border border-gray-400 rounded-lg text-gray-400 flex items-center gap-1"
                        disabled
                      >
                        <ArrowLeft class="size-3" />
                        Previous
                      </button>
                    {:else}
                      <Button
                        class="py-2 px-2 md:px-4 border border-white rounded-lg text-white flex items-center gap-1"
                        processing={loadingStep}
                        on:click={previousStep}
                      >
                        <ArrowLeft class="size-3" />
                        Previous
                      </Button>
                    {/if}
                    {#if !course_progress.purchased && course_progress.step == course.max_free_step && course_progress.substep == course.max_free_substep}
                      <button
                        class="ml-1 py-2 px-2 md:px-4 border border-white rounded-lg text-white flex items-center gap-1"
                        type="button"
                        onclick={purchaseCourse}
                      >
                        <Lock class="size-3" />
                        Unlock Course
                      </button>
                    {:else}
                      <Button
                        class="ml-1 py-2 px-4 border border-white rounded-lg text-white flex items-center gap-1"
                        processing={savingProgress}
                        onclick={() => {
                          saveProgress(
                            course_progress.step,
                            course_progress.substep
                          )
                        }}
                      >
                        Next
                        <ArrowRight class="size-3" />
                      </Button>
                    {/if}
                    <button
                      type="button"
                      onclick={() => {
                        show = false
                      }}
                      class="lg:hidden ml-2 rounded-md text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 flex items-center gap-1"
                    >
                      <span class="sr-only">Close panel</span>
                      <X class="size-6" />
                    </button>
                  </div>
                </div>
              </div>
              <div class="relative mt-6 flex-1 px-4 sm:px-6">
                {#if course_progress.step === 1}
                  <Module1 {course_progress} />
                {:else if course_progress.step === 2}
                  <Module2 {course_progress} />
                {:else if course_progress.step === 3}
                  <Module3 {course_progress} />
                {:else if course_progress.step === 4}
                  <Module4 {course_progress} />
                {:else if course_progress.step === 5}
                  <Module5 {course_progress} />
                {:else if course_progress.step === 6}
                  <Module6 {course_progress} />
                {:else if course_progress.step === 6}
                  <Module6 {course_progress} />
                {:else if course_progress.step === 7}
                  <Module7 {course_progress} />
                {:else if course_progress.step === 8}
                  <Module8 {course_progress} />
                {:else if course_progress.step === 9}
                  <Module9 {course_progress} />
                {/if}

                {#if course_progress.assets.length > 0}
                  <div class="px-12 py-4">
                    <div class="border border-gray-200 bg-white py-4 shadow-lg">
                      <div class="flex items-center justify-center py-3">
                        <button
                          class="bg-blue-500 rounded-md justify-center text-white text-lg py-2 px-12 shadow"
                          type="button"
                          onclick={() => (show_assets = !show_assets)}
                        >
                          {show_assets ? "Hide Assets" : "View Assets"}
                        </button>
                      </div>
                      {#if show_assets}
                        <div
                          class="flex justify-around px-8 py-3 w-full relative border-t border-gray-200"
                          transition:slide
                        >
                          {#each course_progress.assets as asset}
                            <a
                              href="/courses/e1/{asset.href}"
                              target="_blank"
                              rel="noreferrer"
                              class="mt-6 flex flex-col justify-center"
                            >
                              <div class="relative w-24 mx-auto">
                                <img
                                  src={pdf_download}
                                  alt="Download PDF"
                                  class="w-24 hover:opacity-0"
                                />
                                <img
                                  src={pdf_download_hover}
                                  alt="Download PDF"
                                  class="absolute top-0 w-24 opacity-0 hover:opacity-100"
                                />
                              </div>

                              <span class="text-center px-6 mt-1">
                                {asset.name}
                              </span>
                            </a>
                          {/each}
                        </div>
                      {/if}
                    </div>
                  </div>
                {/if}

                {#if !course_progress.purchased}
                  <div class="text-center pb-8 mt-6">
                    <button
                      class="btn big bg-green-600 hover:bg-green-700 text-white inline font-bold text-center"
                      type="button"
                      onclick={purchaseCourse}
                      >Purchase Course & Unlock All Content</button
                    >
                  </div>
                {/if}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  {#if !course_progress.purchased}
    <Modal
      closeButton={true}
      closeOnEsc={true}
      closeOnOuterClick={true}
      bind:open={openPreviewModal}
    >
      <PreviewCourse {course} />
    </Modal>
  {/if}
{:else}
  <button
    class="cursor-pointer"
    onclick={toggleCourse}
    data-name="access-course"
  >
    <img
      src={access_course}
      class="grow w-12 absolute right-0 z-20"
      alt="Access Course"
      transition:fade
    />
  </button>
{/if}

<Modal
  closeButton={true}
  closeOnEsc={true}
  closeOnOuterClick={true}
  bind:open={openPurchaseModal}
  large={true}
>
  <PurchaseCourse
    {stripe_publish_key}
    {stripe_product}
    on:updateProgress={updateProgress}
  />
</Modal>

<style>
  .grow {
    top: 36%;
  }
  @keyframes leaves {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(1.1);
    }
  }
</style>
